import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import deleteGlobalState from "../http/deleteGlobalState";
import getGlobalState, {
  GlobalStateRecord,
  GlobalStateResponse,
} from "../http/getGlobalState";
import postGlobalState, {
  CreateGlobalStateRequest,
} from "../http/postGlobalState";
import putGlobalState, {
  UpdateGlobalStateRequest,
} from "../http/putGlobalState";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const useGlobalStateBatch = () => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const fetchGlobalState = useQuery<GlobalStateResponse>(
    ["global-state"],
    async () => getGlobalState(await getAuthTokenBlocking(getToken, relogin)),
  );

  const byType = useMemo(() => {
    const records = fetchGlobalState.data?.globalState ?? [];
    const map: Record<string, GlobalStateRecord[]> = {};
    for (const record of records) {
      if (!map[record.type]) {
        map[record.type] = [];
      }
      map[record.type].push(record);
    }
    return map;
  }, [fetchGlobalState.data]);

  const globalStateForType = (type: string): GlobalStateRecord[] => byType[type] ?? [];

  const createGlobalState = useMutation(
    async (body: CreateGlobalStateRequest) =>
      postGlobalState(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["global-state"]);
      },
    },
  );

  const updateGlobalState = useMutation(
    async ({ id, ...body }: UpdateGlobalStateRequest & { id: string }) =>
      putGlobalState(await getAuthTokenBlocking(getToken, relogin), id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["global-state"]);
      },
    },
  );

  const removeGlobalState = useMutation(
    async (stateId: string) =>
      deleteGlobalState(await getAuthTokenBlocking(getToken, relogin), stateId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["global-state"]);
      },
    },
  );

  return {
    fetchGlobalState,
    globalStateForType,
    createGlobalState,
    updateGlobalState,
    removeGlobalState,
  };
};

export default useGlobalStateBatch;
